import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createVNode as _createVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { class: "title-text" }
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { class: "title-text" }
const _hoisted_6 = {
  key: 0,
  class: "tag-padding"
}
const _hoisted_7 = { key: 2 }
const _hoisted_8 = { class: "title-text" }
const _hoisted_9 = { key: 3 }
const _hoisted_10 = { class: "title-text" }
const _hoisted_11 = { key: 4 }
const _hoisted_12 = { class: "title-text" }
const _hoisted_13 = {
  key: 1,
  class: "progressbar"
}

import { ref, useTemplateRef, defineAsyncComponent } from 'vue';

import NcButton from '@nextcloud/vue/components/NcButton';
import Modal from './Modal.vue';

import EditDate from './EditDate.vue';
import EditTags from './EditTags.vue';
import EditExif from './EditExif.vue';
import EditLocation from './EditLocation.vue';
import EditOrientation from './EditOrientation.vue';

import { showWarning, showError } from '@services/utils/dialog';
import axios from '@nextcloud/axios';

import { useModal } from '@services/modal';
import { t, n } from '@services/l10n';
import { config } from '@services/user-config';
import { constants } from '@services/constants';
import { API } from '@services/API';
import { isAbortError, useAbort } from '@services/utils/abort-vue';
import * as dav from '@services/dav';
import * as utils from '@services/utils/common';

import type { IExif, IImageInfo, IPhoto } from '@typings';


export default /*@__PURE__*/_defineComponent({
  __name: 'EditMetadataModal',
  setup(__props) {

const NcProgressBar = defineAsyncComponent(() => import('@nextcloud/vue/components/NcProgressBar'));

const modal = useTemplateRef('modal');
const { show, close } = useModal(modal);
const editDate = useTemplateRef<InstanceType<typeof EditDate>>('editDate');
const editTags = useTemplateRef<InstanceType<typeof EditTags>>('editTags');
const editExif = useTemplateRef<InstanceType<typeof EditExif>>('editExif');
const editLocation = useTemplateRef<InstanceType<typeof EditLocation>>('editLocation');
const editOrientation = useTemplateRef<InstanceType<typeof EditOrientation>>('editOrientation');

const photos = ref<IPhoto[] | null>(null);
const sections = ref<number[]>([]);
const processing = ref(false);
const progress = ref(0);
const abort = useAbort();

console.assert(!_m.modals.editMetadata, 'EditMetadataModal created twice');
_m.modals.editMetadata = open;

async function open(photosIn: IPhoto[], sectionsIn: number[] = [1, 2, 3, 4]) {
  const signal = abort.renew();
  show.value = true;
  processing.value = true;
  sections.value = sectionsIn;
  progress.value = 0;

  // Include identical copies hidden by de-duplication (#1299)
  let filtered = photosIn.flatMap((p) => [p, ...(p.dups ?? [])]);

  // Filter out forbidden MIME types
  filtered = filtered.filter((p) => {
    if (constants.FORBIDDEN_EDIT_MIMES.includes(p.mimetype ?? String())) {
      showError(t('memories', 'Cannot edit {name} of type {type}', { name: p.basename!, type: p.mimetype! }));
      return false;
    }

    // Extra filters if orientation is in the sections
    if (sectionsIn.includes(5)) {
      // Videos might work but we don't want to risk it
      if (p.mimetype?.startsWith('video/')) {
        showError(t('memories', 'Cannot edit rotation on videos ({name})', { name: p.basename! }));
        return false;
      }

      // Live photos cannot be edited because the orientation of the video
      // will remain the same and look wrong.
      if (p.liveid) {
        showError(t('memories', 'Cannot edit rotation on Live Photos ({name})', { name: p.basename! }));
        return false;
      }
    }

    return true;
  });

  // Load metadata for all photos
  try {
    await dav.fillImageInfo(
      filtered,
      { tags: 1 },
      (count) => {
        if (!signal.aborted) {
          progress.value = Math.round((count * 100) / filtered.length);
        }
      },
      { signal },
    );
  } catch (e) {
    if (isAbortError(e)) return;
    throw e;
  }

  // Check if already quit
  if (!show.value || signal.aborted) return;

  // Use valid photos
  const valid = filterValid(filtered);
  if (valid.length === 0) {
    close();
    return;
  }

  // Warn user if any raw stacks are present
  if (valid.some((p) => p.stackraw?.length)) {
    showWarning(t('memories', 'Some selected items have stacked RAW files.\nRAW files will not be edited.'));
  }

  photos.value = valid;
  processing.value = false;
}

function cleanup() {
  abort.abort();
  show.value = false;
  photos.value = null;
  processing.value = false;
}

async function save() {
  // Perform validation
  try {
    editDate.value?.validate?.();
  } catch (e: any) {
    console.error(e);
    showError(e);
    return;
  }

  // Start processing
  let done = 0;
  progress.value = 0;
  processing.value = true;

  // Get exif fields diff
  const exifResult = {
    ...(editExif.value?.result?.() ?? {}),
    ...(editLocation.value?.result?.() ?? {}),
  };

  // Tags may be created which might throw
  let tagsResult: { add: number[]; remove: number[] } | null = null;
  try {
    tagsResult = (await editTags.value?.result?.()) ?? null;
  } catch (e: any) {
    processing.value = false;
    console.error(e);
    showError(e);
    return;
  }

  // EXIF update values
  const exifs = new Map<number, IExif>();
  for (const p of photos.value!) {
    // Basic EXIF fields
    const raw: IExif = structuredClone(exifResult as IExif);

    // Date header
    const date = editDate.value?.result?.(p);
    if (date) {
      raw.AllDates = date;
    }

    // Orientation
    const orientation = editOrientation.value?.result?.(p);
    if (orientation != null) {
      raw.Orientation = orientation;
    }

    exifs.set(p.fileid, raw);
  }

  // If a photo has no EXIF date header then updating the metadata will erase
  // the date taken. We need to prompt the user to keep the date taken.
  const hasNoDate = (p: IPhoto) => {
    const exif = p.imageInfo?.exif;
    const hasExifDate = Boolean(exif?.DateTimeOriginal || exif?.CreateDate);
    const isSettingDate = Boolean(exifs.get(p.fileid)!.AllDates);
    return !hasExifDate && !isSettingDate;
  };

  if (
    photos.value!.some(hasNoDate) &&
    (await utils.confirmDestructive({
      title: t('memories', 'Missing date metadata'),
      message: t(
        'memories',
        'Some items may be missing the date metadata. Do you want to attempt copying the currently known timestamp to the metadata (recommended)? Othewise, the timestamp may be reset to the current time.',
      ),
    }))
  ) {
    for (const p of photos.value!) {
      // Check if already has the date taken, or it if can't do anything
      if (!hasNoDate(p) || !p.datetaken) continue;

      // Get the date in EXIF format
      const dateTaken = utils.getExifDateStr(new Date(p.datetaken * 1000));
      const raw = exifs.get(p.fileid);
      raw!.AllDates = dateTaken;
    }
  }

  // Update exif fields
  const calls = photos.value!.map((p) => async () => {
    let dirty = false;
    const fileid = p.fileid;

    try {
      // Update EXIF if required
      const raw = exifs.get(fileid) ?? {};
      if (Object.keys(raw).length > 0) {
        const info = await axios.patch<IImageInfo>(API.IMAGE_SETEXIF(fileid), { raw });
        dirty = true;

        // Update image size
        p.h = info.data?.h ?? p.h;
        p.w = info.data?.w ?? p.w;

        // If orientation was updated we need to change
        // the ETag so that the preview is updated.
        // Deliberately don't change the tag otherwise,
        // so there's no need to re-download the image.
        if (raw.Orientation) {
          p.etag = info.data?.etag ?? p.etag;
        }
      }

      // Update tags if required
      if (tagsResult) {
        await axios.patch<null>(API.TAG_SET(fileid), tagsResult);
        dirty = true;
      }
    } catch (e: any) {
      console.error('Failed to save metadata for', p.fileid, e);
      if (e.response?.data?.message) {
        showError(e.response.data.message);
      } else {
        showError(e);
      }
    } finally {
      // Refresh UX
      if (dirty) {
        p.imageInfo = null;
        utils.bus.emit('files:file:updated', { fileid });
      }

      // Update progress
      done++;
      progress.value = Math.round((done * 100) / (photos.value?.length ?? 100));
    }
  });

  for await (const _ of dav.runInParallel(calls, 8)) {
    // nothing to do
  }

  editOrientation.value?.reset();
  processing.value = false;
  close();

  // Trigger a soft refresh
  utils.bus.emit('memories:timeline:soft-refresh', null);
}

function filterValid(photosIn: IPhoto[]) {
  // Check if we have image info
  const valid = photosIn.filter((p) => p.imageInfo);
  if (valid.length !== photosIn.length) {
    const nPhotos = photosIn.length - valid.length;
    showError(
      n('memories', 'Failed to load metadata for {n} photo.', 'Failed to load metadata for {n} photos.', nPhotos, {
        n: nPhotos,
      }),
    );
  }

  // Check if photos are updatable
  const updatable = valid.filter((p) => p.imageInfo?.permissions?.includes('U'));
  if (updatable.length !== valid.length) {
    const nPhotos = valid.length - updatable.length;
    showError(
      n(
        'memories',
        '{n} photo cannot be edited (permissions error).',
        '{n} photos cannot be edited (permissions error).',
        nPhotos,
        { n: nPhotos },
      ),
    );
  }

  return updatable;
}

return (_ctx: any,_cache: any) => {
  return (_unref(show))
    ? (_openBlock(), _createBlock(Modal, {
        key: 0,
        ref_key: "modal",
        ref: modal,
        onClose: cleanup
      }, {
        title: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)('memories', 'Edit metadata')), 1)
        ]),
        buttons: _withCtx(() => [
          (photos.value)
            ? (_openBlock(), _createBlock(_unref(NcButton), {
                key: 0,
                onClick: save,
                class: "button",
                variant: "error",
                disabled: processing.value
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('memories', 'Save')), 1)
                ]),
                _: 1
              }, 8, ["disabled"]))
            : _createCommentVNode("", true)
        ]),
        default: _withCtx(() => [
          (photos.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                (sections.value.includes(1))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                      _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(t)('memories', 'Date / Time')), 1),
                      _createVNode(EditDate, {
                        ref_key: "editDate",
                        ref: editDate,
                        photos: photos.value,
                        disabled: processing.value,
                        onSave: save
                      }, null, 8, ["photos", "disabled"])
                    ]))
                  : _createCommentVNode("", true),
                (_unref(config).systemtags_enabled && sections.value.includes(2))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                      _createElementVNode("div", _hoisted_5, _toDisplayString(_unref(t)('memories', 'Collaborative Tags')), 1),
                      _createVNode(EditTags, {
                        ref_key: "editTags",
                        ref: editTags,
                        photos: photos.value,
                        disabled: processing.value
                      }, null, 8, ["photos", "disabled"]),
                      (sections.value.length === 1)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_6))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true),
                (sections.value.includes(3))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      _createElementVNode("div", _hoisted_8, _toDisplayString(_unref(t)('memories', 'EXIF Fields')), 1),
                      _createVNode(EditExif, {
                        ref_key: "editExif",
                        ref: editExif,
                        photos: photos.value,
                        disabled: processing.value,
                        onSave: save
                      }, null, 8, ["photos", "disabled"])
                    ]))
                  : _createCommentVNode("", true),
                (sections.value.includes(4))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                      _createElementVNode("div", _hoisted_10, _toDisplayString(_unref(t)('memories', 'Geolocation')), 1),
                      _createVNode(EditLocation, {
                        ref_key: "editLocation",
                        ref: editLocation,
                        photos: photos.value,
                        disabled: processing.value
                      }, null, 8, ["photos", "disabled"])
                    ]))
                  : _createCommentVNode("", true),
                (sections.value.includes(5))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                      _createElementVNode("div", _hoisted_12, _toDisplayString(_unref(t)('memories', 'Orientation (EXIF)')), 1),
                      _createVNode(EditOrientation, {
                        ref_key: "editOrientation",
                        ref: editOrientation,
                        photos: photos.value,
                        disabled: processing.value
                      }, null, 8, ["photos", "disabled"])
                    ]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          (processing.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                _createVNode(_unref(NcProgressBar), {
                  value: progress.value,
                  error: true
                }, null, 8, ["value"])
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 512))
    : _createCommentVNode("", true)
}
}

})